import type { EnrollmentStatus, TermState } from '@repo/shared-types';

/**
 * A row this rule can classify — the workbench's own minimal projection of an
 * enrollment list row, so the pure function doesn't depend on the wider
 * `EnrollmentWithSubject` shape (pricing, dates, slots, …) it will never read.
 */
export interface TermNudgeRow {
  enrollmentId: string;
  studentId: string;
  studentName: string;
  /**
   * PAUSED is deliberately eligible here, not just ACTIVE: the absence-rule
   * bulk apply added in this same branch (`POST /enrollments/charge-absent/bulk`)
   * targets `status: { in: ['ACTIVE', 'PAUSED'] }` because "a paused student
   * resumes under the new rule" — this branch already treats PAUSED as live
   * billing state. A student paused mid-khoá who already over-ran their block
   * must not go silent just because they're paused.
   */
  status: EnrollmentStatus;
  /** `null` for a MONTHLY_FLAT/PER_SESSION+MONTHLY enrollment — those never carry a khoá. */
  termState: TermState | null;
}

export interface TermNudgeState {
  show: boolean;
  out: TermNudgeRow[];
  nearlyOut: TermNudgeRow[];
}

/**
 * Splits an enrollment list into "khoá ran out" and "khoá nearly out" — the
 * workbench's only signal that a term student's prepaid block needs
 * collecting, now that the roster gate is gone (M-lifecycle Task 3): a
 * student whose block ran out used to be silently dropped from upcoming
 * rosters, which was itself the (accidental) signal that something needed
 * attention. Now they stay on the roster and the overage bills at renewal,
 * so nothing else in the product says "this one needs collecting" — this does.
 *
 * Rules, and why:
 *   - Only ACTIVE and PAUSED enrollments are considered. ENDED has already left
 *     and needs settling as part of ending it, not nudging about here; PAUSED
 *     is included on purpose — see the field doc on `TermNudgeRow.status`.
 *   - A `null` termState is a MONTHLY enrollment; it never carries a khoá and
 *     is never listed here (mirrors StudentDetailPage, which only renders its
 *     own term UI inside `e.termState && …`).
 *   - `hasOpenTerm: false` means the term hasn't started (or was never
 *     started) yet. `computeTermState` reports `remaining: 0` in that case
 *     purely as a placeholder — treating that as "out" would nudge to collect
 *     from a student who hasn't even begun a block. Both buckets require an
 *     open term.
 *   - out: `remaining <= 0` — the block is exactly spent (0) or over-run
 *     (negative). Either way, sessions already happened that the block
 *     doesn't cover: collect now.
 *   - nearlyOut: `remaining > 0 && remaining <= 2` — the same `<= 2` cutoff
 *     `StudentDetailPage` already uses for its own per-student badge, so the
 *     workbench and the student page can never disagree about who is close
 *     to running out: collect soon.
 *
 * Both buckets sort ascending by `remaining` — the most negative (or, within
 * nearlyOut, the smallest positive) value first. Without a "when did this
 * start running over" timestamp, the size of the overage/shortfall is the
 * best proxy for how long it's been outstanding, so the row most overdue for
 * a conversation with the parent leads the list.
 */
export function termNudgeState(rows: TermNudgeRow[]): TermNudgeState {
  const withOpenTerm = rows.filter(
    (r): r is TermNudgeRow & { termState: TermState } =>
      (r.status === 'ACTIVE' || r.status === 'PAUSED') && r.termState !== null && r.termState.hasOpenTerm,
  );

  const byRemainingAsc = (a: { termState: TermState }, b: { termState: TermState }) =>
    a.termState.remaining - b.termState.remaining;

  const out = withOpenTerm.filter((r) => r.termState.remaining <= 0).sort(byRemainingAsc);
  const nearlyOut = withOpenTerm
    .filter((r) => r.termState.remaining > 0 && r.termState.remaining <= 2)
    .sort(byRemainingAsc);

  return { show: out.length > 0 || nearlyOut.length > 0, out, nearlyOut };
}
